import React, { useRef, useEffect, forwardRef } from "react"
import { Input, InputNumber, Form } from "antd"
import PropTypes from "prop-types"
import { ReadOnlyField } from "components-design-system"
import "./TextInput.scss"

const { TextArea } = Input

const TextInput = forwardRef(function TextInput(props, ref) {
  const {
    size = "middle",
    type = "text",
    readOnly,
    defaultValue,
    placeholder, // read-only fieldName
    label,
    onChange, // returns SyntheticBaseEvent // evt.target.value
    rows = 2, // "textarea"
    focusOnMount = false,
    tooltip = null, // { title, align } // TODO: add tooltip capabilities again
    className,
    ellipsis = false,
    formItemClassName,
    labelLayout = "vertical",
    allowClear, // default true for antd Input // will cause antd TextArea to overflow outside its container
    ...moreProps // id, value (controlled), allowClear, onBlur, prefix, style, status ("error")
  } = props
  const inputRef = useRef(null)
  const classNames = ["prime-text-input"]
  if (className) classNames.push(className)
  if (readOnly) classNames.push("read-only")

  React.useImperativeHandle(
    ref,
    () => {
      return {
        focus: () => inputRef?.current.focus({ cursor: "end" }),
        value: inputRef?.current?.resizableTextArea?.textArea?.value,
      }
    },
    [inputRef?.current]
  )

  // call parent function
  const handleChange = (evt) => {
    if (onChange) {
      onChange(evt) // evt.target.id, evt.target.value
    }
  }

  // when set focusOnMount
  useEffect(() => {
    if (focusOnMount) inputRef?.current?.focus({ cursor: "end" })
  }, [])

  const renderInput = (id) => {
    if (readOnly) {
      return (
        <ReadOnlyField
          size={size}
          placeholder={placeholder}
          ellipsis={ellipsis}
        >
          {moreProps?.value || defaultValue}
        </ReadOnlyField>
      )
    }

    if (type === "password") {
      return (
        <Input.Password
          ref={inputRef}
          id={id}
          size={size}
          className={classNames.join(" ")}
          defaultValue={defaultValue}
          placeholder={placeholder}
          onChange={handleChange}
          {...moreProps}
        />
      )
    } else if (type === "textarea") {
      return (
        <TextArea
          ref={inputRef}
          id={id}
          placeholder={placeholder}
          onChange={handleChange}
          size={size}
          className={classNames.join(" ")}
          defaultValue={defaultValue}
          autoSize={true} // currently buggy, when set creates an empty textarea input at the top of the page // 8 Jan 25 - not seeing this issue anymore, setting to auto
          rows={rows}
          allowClear={allowClear}
          {...moreProps}
        />
      )
    } else if (type === "number") {
      return (
        <InputNumber
          ref={inputRef}
          id={id}
          size={size}
          className={classNames.join(" ")}
          defaultValue={defaultValue}
          placeholder={placeholder}
          onChange={handleChange}
          allowClear={allowClear}
          {...moreProps}
        />
      )
    }

    return (
      <Input
        ref={inputRef}
        id={id}
        size={size}
        className={classNames.join(" ")}
        defaultValue={defaultValue}
        placeholder={placeholder}
        onChange={handleChange}
        allowClear={allowClear}
        {...moreProps}
      />
    )
  }

  if (label) {
    return (
      <Form layout={labelLayout}>
        <Form.Item
          label={label}
          className={`prime-form-item${formItemClassName ? " " + formItemClassName : ""}`}
          colon={false}
          tooltip={props?.tooltip}
        >
          {renderInput(label)}
        </Form.Item>
      </Form>
    )
  }

  return renderInput(label)
})

TextInput.propTypes = {
  size: PropTypes.oneOf(["small", "medium", "large"]),
  type: PropTypes.oneOf(["text", "password", "textarea"]),
  readOnly: PropTypes.bool,
  defaultValue: PropTypes.string,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  rows: PropTypes.number,
  focusOnMount: PropTypes.bool,
  tooltip: PropTypes.shape({
    title: PropTypes.string,
    align: PropTypes.object,
  }),
  className: PropTypes.string,
  labelLayout: PropTypes.string,
  ellipsis: PropTypes.bool, // for readOnly
  allowClear: PropTypes.bool,
}

export default React.memo(TextInput)
